import React from "react";
import {
    Box,
    Button,
    Collapse,
    Divider,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddLocationAltOutlinedIcon from '@mui/icons-material/AddLocationAltOutlined';
import { DimensionsPreviewStruct } from "../../models/ArtworkMeasurement";
import { Artwork } from "../../models/Artwork";

type DetailRowProps = {
    icon?: React.ReactNode;
    label: string;
    value?: React.ReactNode;
};

const DetailRow: React.FC<DetailRowProps> = ({ icon, label, value }) => {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "24px 88px 1fr", sm: "24px 110px 1fr" },
                gap: 1,
                alignItems: "start",
                minWidth: 0,
            }}
        >
            <Box sx={{ pt: 0.2, color: "text.secondary" }}>
                {icon}
            </Box>

            <Typography
                variant="body2"
                sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                }}
            >
                {label}
            </Typography>

            <Typography
                variant="body2"
                sx={{
                    minWidth: 0,
                    wordBreak: "break-word",
                }}
            >
                {value || "—"}
            </Typography>
        </Box>
    );
};

type ArtworkDetailsSectionProps = {
    artwork: Artwork;
    categoryPath: string;
    locationPath: string;
    dimensionsPreview: DimensionsPreviewStruct;
    canEditArtwork: boolean;
    isAdmin: boolean;
    isViewOnly: boolean;
    onEdit: () => void;
    onDelete: () => void;
    onMove: () => void;
    statusHeadline?: string;
    additionalDetails?: Array<{
        label: string;
        value?: React.ReactNode;
    }>;
};

const ArtworkDetailsSection: React.FC<ArtworkDetailsSectionProps> = ({
    artwork,
    categoryPath,
    locationPath,
    dimensionsPreview,
    canEditArtwork,
    isAdmin,
    isViewOnly,
    onEdit,
    onDelete,
    onMove,
    statusHeadline,
    additionalDetails = [],
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [showMore, setShowMore] = React.useState(false);

    const primaryDetails = [
        {
            label: "Category",
            value: categoryPath,
            icon: <CategoryOutlinedIcon fontSize="small" />,
        },
        {
            label: "Location",
            value: locationPath,
            icon: <PlaceOutlinedIcon fontSize="small" />,
        },
        {
            label: dimensionsPreview.label,
            value: dimensionsPreview.value,
            icon: <StraightenOutlinedIcon fontSize="small" />,
        },
        {
            label: "Status",
            value: statusHeadline,
            icon: <InfoOutlinedIcon fontSize="small" />,
        },
    ];

    const extraDetails = additionalDetails.filter(
        d => d.value && `${d.value}`.trim() !== ""
    );

    return (
        <section className="artwork-section details-section">
            <div className="section-header">
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        justifyContent: "space-between",
                        alignItems: { xs: "stretch", sm: "flex-start" },
                        gap: 1.5,
                        minHeight: { xs: 0, sm: 40 },
                        width: { xs: "100%", sm: "auto" },
                    }}
                >
                <Typography variant="h3" sx={{ fontWeight: 600 }}>
                    Artwork Details
                    </Typography>
                </Box>
            </div>

            <div className="section-content">
                <Box
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        p: { xs: 1.5, sm: 2 },
                        bgcolor: "background.paper",
                    }}
                >
                    <Stack spacing={1.25}>
                        {primaryDetails.map((item) => (
                            <DetailRow
                                key={item.label}
                                icon={item.icon}
                                label={item.label}
                                value={item.value}
                            />
                        ))}

                        {isMobile && extraDetails.length > 0 && (
                            <>
                                <Divider sx={{ my: 0.5 }} />

                                <Box>
                                    <Button
                                        size="small"
                                        onClick={() => setShowMore(prev => !prev)}
                                        endIcon={showMore ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        sx={{ px: 0 }}
                                    >
                                        {showMore ? "Show fewer details" : "Show more details"}
                                    </Button>
                                </Box>

                                <Collapse in={showMore}>
                                    <Stack spacing={1.25} sx={{ pt: 1 }}>
                                        {extraDetails.map((item) => (
                                            <DetailRow
                                                key={item.label}
                                                label={item.label}
                                                value={item.value}
                                            />
                                        ))}
                                    </Stack>
                                </Collapse>
                            </>
                        )}

                        {!isMobile && extraDetails.length > 0 && (
                            <>
                                <Divider sx={{ my: 0.5 }} />
                                <Stack spacing={1.25}>
                                    {extraDetails.map((item) => (
                                        <DetailRow
                                            key={item.label}
                                            label={item.label}
                                            value={item.value}
                                        />
                                    ))}
                                </Stack>
                            </>
                        )}
                    </Stack>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ mt: 1.5, display: "flex", gap: 1, flexWrap: "wrap" }}>

                        {!isViewOnly && canEditArtwork && (
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<EditOutlinedIcon />}
                                onClick={onEdit}
                                fullWidth={true}
                            >
                                Edit Details
                            </Button>
                        )}

                        {!isViewOnly && canEditArtwork && (
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AddLocationAltOutlinedIcon />}
                                onClick={onMove}
                                fullWidth={true}
                            >
                                Record Movement
                            </Button>
                        )}

                        {isAdmin && !isViewOnly && (
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<DeleteOutlineIcon />}
                                onClick={onDelete}
                                fullWidth={true}
                            >
                                Delete
                            </Button>
                        )}
                    </Box>
                </Box>
            </div>
        </section>
    );
};

export default ArtworkDetailsSection;